export function formatDateShort(dateString: string | null | undefined): string {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }

    // Get month as short name (Jan, Feb, etc.)
    const month = date.toLocaleString('en-US', { month: 'short' });

    // Get day of month (1-31)
    const day = date.getDate();

    const year = date.getFullYear();

    // Return formatted string
    return `${month} ${day}, ${year}`;
  } catch (error) {
    return 'N/A';
  }
}

export function formatTimeRemaining(
  dateString: string | null | undefined
): string | null {
  if (!dateString) return null;

  try {
    const target = new Date(dateString);
    if (isNaN(target.getTime())) return null;

    const now = Date.now();
    const diffMs = target.getTime() - now;

    if (diffMs < 0) return 'Overdue';

    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    const all: string[] = [];
    if (days > 0) all.push(`${days}d`);
    if (hours > 0) all.push(`${hours}h`);
    if (minutes > 0) all.push(`${minutes}m`);

    const parts = all.length > 0 ? all.slice(0, 2) : ['0m'];

    return `in ${parts.join(' ')}`;
  } catch {
    return null;
  }
}

export function formatDateWithYear(
  dateString: string | null | undefined
): string {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }

    // Get month as short name (Jan, Feb, etc.)
    const month = date.toLocaleString('en-US', { month: 'short' });

    // Get day of month (1-31)
    const day = date.getDate();

    // Get full year (e.g., 2024)
    const year = date.getFullYear();

    // Return formatted string
    return `${month} ${day}, ${year}`;
  } catch (error) {
    return 'N/A';
  }
}

// True when any two snapshots are less than a day apart — i.e. labels need time, not
// just a date, to be distinguishable (hourly / 30-min watches).
export function isSubDaily(dates: (string | null | undefined)[]): boolean {
  const sorted = dates
    .filter((d): d is string => !!d)
    .map((d) => new Date(d).getTime())
    .filter((t) => !isNaN(t))
    .sort((a, b) => a - b);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] - sorted[i - 1] < 24 * 60 * 60 * 1000) return true;
  }
  return false;
}

// Label one snapshot at the resolution implied by the whole set: day (+ time when the
// snapshots are sub-daily). Used by the timeline x-axis AND the "compare with" picker so
// they always agree.
export function formatVersionLabel(
  allDates: (string | null | undefined)[],
  date: string | null | undefined,
): string {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'N/A';
  return isSubDaily(allDates)
    ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}
